/**
 * This file is for defining common selectors that can be used across the app.
 */

import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import _get from 'lodash/get';
import ReduxStore from 'src/types/store/reduxStore';
import { shouldShowNewAtlas } from 'src/utils/app';
import { buildAppUrl } from 'src/components/common/Logo/Logo';
import { encodeHash } from 'src/utils/hashIds';
import { createSelector } from '@reduxjs/toolkit';

export const allSegmentsSelector = createSelector(
  (state) => state.segments,
  (segments) => [...segments.mySegments, ...segments.teamSegments]
);

/**
 * Return a combined list of user created segments and team segments
 */
export const useAllSegments = () => {
  return useAppSelector(allSegmentsSelector);
};

export const allSearchTermListsSelector = createSelector(
  (state) => state.segments,
  (segments) => [...segments.teamSearchTermLists, ...segments.mySearchTermLists]
);

/**
 * Return a combined list of user created search term lists and team search term lists
 */
export const useAllSearchTermLists = () => {
  return useAppSelector(allSearchTermListsSelector);
};

export const allSegmentsAndSearchTermListsSelector = createSelector(
  (state) => state.segments,
  (segments) => {
    const { mySegments, teamSegments, teamSearchTermLists, mySearchTermLists } = segments;
    return [...mySegments, ...teamSegments, ...teamSearchTermLists, ...mySearchTermLists];
  }
);

/**
 * Return a combined list of user created segments and team segments and user created search term lists and team search term lists
 */
export const useAllSegmentsAndSearchTermLists = () => {
  return useAppSelector(allSegmentsAndSearchTermListsSelector);
};

/**
 * Get the main entity type
 */
export const useMainEntityType = () => {
  return useAppSelector((state) => state?.entityService?.mainEntity?.type);
};

/**
 * Get the main entity name
 */
export const useMainEntityName = () => {
  return useAppSelector((state) => state?.entityService?.mainEntity?.name);
};

/**
 * Get the user id
 */
export const useUserId = () => {
  return useAppSelector((state) => _get(state, ['user', 'session', 'userId']));
};

/**
 * Get the main entity id
 */
export const useMainEntityId = () => {
  return useAppSelector((state) => state.entityService?.mainEntity?.id);
};

export const useMainEntity = () => {
  return useAppSelector((state) => state.entityService.mainEntity);
};

/**
 * Get the retailer id
 */
export const useRetailerId = () => {
  return useAppSelector((state) => _get(state, ['retailer', 'id']));
};

export const useRetailerSku = () => {
  return useAppSelector((state) => state.entityService?.mainEntity?.retailerSku);
};

/**
 * Get the retailer display name
 */
export const useRetailerDisplayName = () => {
  const retailerId = useRetailerId();
  const availableRetailers = useAppSelector((state) => state.retailer.availableRetailers);
  const retailers = availableRetailers;
  const retailerObj = retailers.find((r) => r.id === retailerId);
  if (retailerObj) {
    return retailerObj.displayName;
  }

  // Default, return retailer Id if no match found
  return retailerId;
};

/**
 * Get the beacon client id]
 */
export const useBeaconClientId = () => {
  return useAppSelector((state) => _get(state, ['user', 'config', 'vendor', 'BeaconClientId']));
};

export const useCompanyId = () => {
  return useAppSelector((state) => _get(state, ['user', 'session', 'companyId']));
};

export const useUserEmail = () => {
  return useAppSelector((state) => _get(state, ['user', 'session', 'email']));
};

/**
 * Returns a boolean indicating if the main entity is a product and the product entity if it exists
 */
export const useProductEntity = (): {
  productEntity: ReduxStore['entityService']['mainEntity'] | null;
  isProductEntity: boolean;
} => {
  return useAppSelector((state) => {
    if (_get(state, ['entityService', 'mainEntity', 'type']) === 'product') {
      return { productEntity: _get(state, ['entityService', 'mainEntity'], null), isProductEntity: true };
    }

    return { productEntity: null, isProductEntity: false };
  });
};
/**
 * Returns a boolean indicating if the main entity is a category and the category entity if it exists
 */
export const useCategoryEntity = (): {
  categoryEntity: ReduxStore['entityService']['mainEntity'] | null;
  isCategoryEntity: boolean;
  isAllCategoriesEntity: boolean;
} => {
  return useAppSelector((state) => {
    if (_get(state, ['entityService', 'mainEntity', 'type']) === 'category') {
      return {
        categoryEntity: _get(state, ['entityService', 'mainEntity'], null),
        isCategoryEntity: true,
        isAllCategoriesEntity: _get(state, ['entityService', 'mainEntity', 'id']) === '0'
      };
    }

    return { categoryEntity: null, isCategoryEntity: false, isAllCategoriesEntity: false };
  });
};
/**
 * Returns a boolean indicating if the main entity is a subcategory and the subcategory entity if it exists
 */
export const useSubcategoryEntity = (): {
  subcategoryEntity: ReduxStore['entityService']['mainEntity'] | null;
  isSubcategoryEntity: boolean;
} => {
  return useAppSelector((state) => {
    if (_get(state, ['entityService', 'mainEntity', 'type']) === 'subcategory') {
      return { subcategoryEntity: _get(state, ['entityService', 'mainEntity'], null), isSubcategoryEntity: true };
    }

    return { subcategoryEntity: null, isSubcategoryEntity: false };
  });
};

export const useTrendEntity = (): {
  trendEntity: ReduxStore['entityService']['mainEntity'] | null;
  isTrendEntity: boolean;
} => {
  return useAppSelector((state) => {
    if (_get(state, ['entityService', 'mainEntity', 'type']) === 'microtrend') {
      return { trendEntity: _get(state, ['entityService', 'mainEntity'], null), isTrendEntity: true };
    }

    return { trendEntity: null, isTrendEntity: false };
  });
};

export const useBrandEntity = (): {
  brandEntity: ReduxStore['entityService']['mainEntity'] | null;
  isBrandEntity: boolean;
} => {
  return useAppSelector((state) => {
    if (_get(state, ['entityService', 'mainEntity', 'type']) === 'brand') {
      return { brandEntity: _get(state, ['entityService', 'mainEntity'], null), isBrandEntity: true };
    }

    return { brandEntity: null, isBrandEntity: false };
  });
};

export const useSegmentEntity = (): {
  segmentEntity: ReduxStore['entityService']['mainEntity'] | null;
  isSegmentEntity: boolean;
} => {
  return useAppSelector((state) => {
    if (_get(state, ['entityService', 'mainEntity', 'type']) === 'segment') {
      return { segmentEntity: _get(state, ['entityService', 'mainEntity'], null), isSegmentEntity: true };
    }

    return { segmentEntity: null, isSegmentEntity: false };
  });
};

export const useBusinessUnitEntity = (): {
  businessUnitEntity: ReduxStore['entityService']['mainEntity'] | null;
  isBusinessUnitEntity: boolean;
} => {
  return useAppSelector((state) => {
    if (_get(state, ['entityService', 'mainEntity', 'type']) === 'businessunit') {
      return { businessUnitEntity: _get(state, ['entityService', 'mainEntity'], null), isBusinessUnitEntity: true };
    }

    return { businessUnitEntity: null, isBusinessUnitEntity: false };
  });
};

/**
 * When on the topic view, gets the name of the company
 * that owns the topic
 */
export const useCompanyName = (): string => {
  const companyName = useAppSelector((state) => state?.user?.config?.vendor?.CompanyName);

  return companyName;
};

export const useUserProfile = () => {
  return useAppSelector((state) => state.user?.config?.profile);
};

export const useAppSubscribed = (): {
  [appName: string]: {
    id: string;
    path: string;
    shouldBeEnabled: () => boolean;
    getLabel: () => string;
  };
} => {
  const isRestrictedDemoUser = useAppSelector((state) => state.user.config.isRestrictedDemoUser);
  const app = useAppSelector((state) => state.app);
  const retailer = useAppSelector((state) => state.retailer);
  const user = useAppSelector((state) => state.user);
  const homeMap = {
    atlas: isRestrictedDemoUser
      ? `/home&tab=summary&subtab=atlas&entityType=category`
      : `/company/${encodeHash(0)}?rid=${retailer.id}`,
    advertising: `/overview&tab=${app.defaultQueryParams.tab}&subtab=${app.defaultQueryParams.subtab}`,
    omni: `/client/${user.config.vendor.BeaconClientId}`,
    beacon: ''
  };
  const appStage = useAppSelector((state) => state.app.stage);
  const appSubscriptions = useAppSelector((state) => state.user?.config?.appSubscriptions);

  const APP_DETAILS = {
    atlas: {
      id: 'atlas',
      path: buildAppUrl('atlas', homeMap.atlas, appStage),
      shouldBeEnabled: () => {
        return appSubscriptions.includes('atlas') || appSubscriptions.includes('atlas-pro');
      },
      getLabel: () => {
        return 'Atlas';
      }
    },
    beacon: {
      id: 'beacon',
      path: buildAppUrl('beacon', homeMap.beacon, appStage),
      shouldBeEnabled: () => {
        return appSubscriptions.includes('beacon') || appSubscriptions.includes('beacon-pro');
      },
      getLabel: () => {
        return 'Beacon';
      }
    },
    advertising: {
      id: 'drive',
      path: buildAppUrl('ad', homeMap.advertising, appStage),
      shouldBeEnabled: () => {
        return appSubscriptions.includes('advertising');
      },
      getLabel: () => {
        return 'Drive';
      }
    },
    omni: {
      id: 'omni',
      path: buildAppUrl('omni', homeMap.omni, appStage),
      shouldBeEnabled: () => {
        return appSubscriptions.includes('omni');
      },
      getLabel: () => {
        return 'Omni';
      }
    },
    shopperos: {
      id: 'shopperos',
      path: 'https://shopperos.stackline.com/',
      shouldBeEnabled: () => {
        return true;
      },
      getLabel: () => {
        return 'Shopper OS';
      }
    }
  };

  return APP_DETAILS;
};

/** For Atlas this will include all brands 0 - */
export const useBrandsFollowingIds = (): number[] => {
  const brands = useAppSelector((state) => state?.brandsFollowing);
  return brands.map((brand) => brand.id).filter((brand) => brand !== 0);
};

export const categoryIdsSelector = createSelector(
  (state) => state.categories,
  (categories) => categories.map((category) => category.id)
);

export const categoriesByRetailerIdSelector = createSelector(
  [(state) => state.retailer, (state) => state.categoriesByRetailerId],
  (retailer, categoriesByRetailerId) => categoriesByRetailerId[retailer.id]?.map((category) => category.id)
);

export const useCategoryIds = (): number[] => {
  const categoryIds = useAppSelector(categoryIdsSelector);
  const categoriesByRetailerId = useAppSelector(categoriesByRetailerIdSelector);
  const mainEntity = useMainEntity();

  const entityCategoryIds = mainEntity?.categoryIds ?? [];
  const entityType = mainEntity?.type;

  if (shouldShowNewAtlas()) {
    return categoriesByRetailerId;
  }

  if (entityType === 'segment') {
    return categoryIds;
  }

  return entityCategoryIds?.filter((id) => categoryIds.includes(id));
};

export const useTimePeriods = () => {
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);

  return { mainTimePeriod, comparisonTimePeriod };
};

export const useCategoryCount = () => useAppSelector((state) => state.categories?.length);
